import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilState, useResetRecoilState } from "recoil";
import _ from "lodash";
import { useMutation } from "@apollo/react-hooks";
import { Box, Button, Card } from "@mui/material";
import { MyHrBackToPage } from "my-hr/components";
import {
  inactiveGroupEmployeeList,
  inactiveGroupEmployeeListIsMobile,
} from "my-hr/configs/my-hr-table-defs";
import { MY_HR_INACTIVATE_USERS } from "my-hr/graphql";
import { useMyHrFilteredList, useMyHrFilteredListCount } from "my-hr/hooks";
import {
  OcCard,
  OcConfirmationDialog,
  OcDataTable,
  OcDatePicker,
  OcPageTransition,
  OcRoleFilter,
} from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcButtonGroup,
  useOcTableState,
  useOcIsMobile,
  useOcSnackbar,
} from "oc/hooks";
import { getDateFormatByLocale } from "oc/utils";
import { myHrRoleFilterState } from "states";

export const MyHRAdminInactivateGroupPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  const [inactivateDate, setInactivateDate] = useState(new Date());
  const [errorState, setErrorState] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(
    undefined,
  );

  const buttonsDef = ["OC_ACTIVE", "OC_ALL"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: "MyHRAdminInactiveGroupPageButtonGroup",
    buttonsDef,
  });

  const [roleFilters, setRoleFilters] = useRecoilState(myHrRoleFilterState);

  let tableId = "filteredListTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    pageSize: 5,
  });

  const { data: filteredListCount } = useMyHrFilteredListCount({
    isActive: selectedButton === "OC_ACTIVE",
    roleFilters,
    useOrLogic: true,
  });

  const { data: filteredList, loading } = useMyHrFilteredList({
    page: tableData.page,
    pageSize: tableData.pageSize,
    isActive: selectedButton === "OC_ACTIVE",
    roleFilters,
    useOrLogic: true,
  });

  const { setOpenMessage } = useOcSnackbar();

  const [inactivateUsers] = useMutation(MY_HR_INACTIVATE_USERS, {
    onCompleted: () => {
      setOpenMessage({
        type: "success",
        message: t("MY_HR_INACTIVATE_USERS_SUCCESSFULLY"),
      });
      handleCancel();
    },
    onError: (error) => {
      console.log({ error });
      setOpenMessage({
        type: "error",
        message: t("MY_HR_INACTIVATE_USERS_FAILED"),
      });
    },
  });

  const handleAddFilter = ({ key, value }: { key: string; value: any }) => {
    const newState = _.cloneDeep(roleFilters);
    newState[key] = newState[key].concat([value]);
    setRoleFilters(newState);
  };

  const handleRemoveFilter = ({ key, value }: { key: string; value: any }) => {
    const newState = _.cloneDeep(roleFilters);
    newState[key] = newState[key].filter((item: any) => item !== value);
    setRoleFilters(newState);
  };

  const resetRoleFilters = useResetRecoilState(myHrRoleFilterState);

  const handleInactivateDate = (date: Date | null) => {
    if (date) {
      setInactivateDate(date);
    }
  };

  const inactivateDateError = new Date(inactivateDate).toLocaleDateString();

  const handleInactivateUsers = () => {
    const params = {
      inactivateDate,
      roleFilters,
    };
    inactivateUsers({ variables: params });
  };

  const handleCancel = () => {
    resetRoleFilters();
    history.push("/my-hr/users");
  };

  const [showConfirmationDialog, setShowConfirmationDialog] =
    React.useState(false);

  const handleShowConfirmationDialog = () => {
    if (inactivateDateError === "Invalid Date") {
      setErrorState(true);
      return;
    }
    setShowConfirmationDialog(true);
  };

  const handleOKConfirmationDialog = () => {
    setShowConfirmationDialog(false);
    handleInactivateUsers();
  };

  const handleCancelConfirmationDialog = () => {
    setShowConfirmationDialog(false);
  };

  const generateConfirmationText = () => {
    if (
      roleFilters.company.length === 0 &&
      roleFilters.orgUnit.length === 0 &&
      roleFilters.workingPlace.length === 0 &&
      roleFilters.jobClassGroup.length === 0 &&
      roleFilters.payOffice.length === 0 &&
      roleFilters.contract.length === 0
    ) {
      return "MY_HR_INACTIVATE_USERS_TO_ALL_DIALOG_TEXT";
    }
    return "MY_HR_INACTIVATE_USERS_DIALOG_TEXT";
  };

  const tableDef = isMobile
    ? inactiveGroupEmployeeListIsMobile
    : inactiveGroupEmployeeList;

  const filteredTableDef = {
    id: inactiveGroupEmployeeList?.id,
    columns: inactiveGroupEmployeeList?.columns?.filter(
      (column) =>
        !inactiveGroupEmployeeListIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const onSelectRow = (o: { _id?: string } | null) => {
    if (isMobile) {
      const id = o?._id ?? undefined;
      setSelectedRowId(selectedRowId === id ? undefined : id);
    }
  };

  return (
    <OcPageTransition>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="100%"
      >
        <OcConfirmationDialog
          showDialog={showConfirmationDialog}
          title={t("MY_HR_INACTIVATE_USERS_DIALOG_TITLE")}
          texts={[t(generateConfirmationText())]}
          okButtonText={t("MY_HR_INACTIVATE")}
          cancelButtonText={t("OC_CANCEL")}
          onClose={handleOKConfirmationDialog}
          onCancel={handleCancelConfirmationDialog}
        />
        <Box padding={isMobile ? "16px 0px" : "16px"}>
          <MyHrBackToPage
            label={t("MY_HR_INACTIVATE_GROUP_TITLE")}
            onBack={handleCancel}
            isMobile={isMobile}
          />
          <Box height="16px" />
          <OcCard label={t("MY_HR_USERS")}>
            <>
              <OcRoleFilter
                subtypeProps={{
                  queryType: "admin",
                  handleAddItem: handleAddFilter,
                  handleRemoveItem: handleRemoveFilter,
                  roleFilters,
                  product: "MyHR",
                  role: "admin",
                }}
                OcButtonGroup={OcButtonGroup}
                summaryItems={[
                  {
                    label: "OC_COUNT_OF_EMPLOYEES",
                    value: filteredListCount,
                    color: "primary",
                  },
                ]}
                buttonBarProps={{
                  handleRightButton: resetRoleFilters,
                  primaryButtonText: "",
                  noLine: true,
                  paddingTop: "10px",
                }}
              />
              <Box paddingTop="32px">
                <OcDataTable
                  data={filteredList}
                  loading={loading}
                  tableDef={tableDef}
                  tableData={tableData}
                  setTableData={setTableData}
                  pagination={true}
                  onSelectRow={onSelectRow}
                  filteredTableDef={filteredTableDef}
                  selectedRowId={selectedRowId}
                />
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                paddingTop="24px"
              >
                <Card
                  variant="outlined"
                  style={{
                    width: isMobile ? "100%" : "696px",
                    borderRadius: "4px",
                    borderColor: "white",
                    overflow: "visible",
                    marginBottom: "16px",
                    backgroundColor: "#F1F3F4",
                  }}
                >
                  <Box padding="16px" display="flex" flexDirection="column">
                    <Box
                      fontSize="20px"
                      letterSpacing="0.15px"
                      padding="15px 0px"
                      paddingBottom="30px"
                    >
                      {t("MY_HR_INACTIVATE")}
                    </Box>
                    <Box
                      marginBottom="36px"
                      width={isMobile ? "100%" : "432px"}
                    >
                      <OcDatePicker
                        error={errorState}
                        helperText={
                          errorState ? t("OC_NOT_SELECTABLE") : undefined
                        }
                        id="validFrom"
                        label={t("OC_VALID_TO")}
                        minDate={new Date()}
                        onChange={handleInactivateDate}
                        value={inactivateDate}
                        format={getDateFormatByLocale(i18n.language)}
                      />
                    </Box>
                    <Box
                      id="my-hr-add-user-role-button-cancel"
                      display="flex"
                      flexDirection={isMobile ? "column-reverse" : "row"}
                      justifyContent="flex-end"
                      padding={isMobile ? "0px" : "10px 0px 0px 0px"}
                    >
                      <Button
                        variant="text"
                        color="secondary"
                        onClick={() => handleInactivateDate(new Date())}
                      >
                        {t("OC_CANCEL")}
                      </Button>
                      {isMobile && <Box height="16px" />}
                      <Button
                        id="my-hr-add-user-role-button"
                        variant="contained"
                        color="primary"
                        disableElevation
                        style={{ marginLeft: isMobile ? "0px" : "16px" }}
                        //disabled={!selectedRole}
                        onClick={handleShowConfirmationDialog}
                      >
                        {inactivateDate
                          ? t("MY_HR_INACTIVATE_BUTTON")
                          : t("OC_SAVE")}
                      </Button>
                    </Box>
                  </Box>
                </Card>
              </Box>
            </>
          </OcCard>
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyHRAdminInactivateGroupPage;
